<template>
  <div class="fixed inset-x-0 top-6 z-50 flex justify-center px-4">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@24,400,0,0" rel="stylesheet">
    <nav 
      class="relative flex h-14 w-full max-w-5xl items-center justify-between rounded-full border border-white/20 bg-white/70 px-2 pl-6 shadow-[0_8px_32px_rgba(0,0,0,0.12)] backdrop-blur-2xl backdrop-saturate-150 transition-all dark:border-white/10 dark:bg-zinc-900/60 dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
    >
      <router-link to="/" class="flex shrink-0 items-center gap-2 outline-none transition-opacity hover:opacity-80">
        <div class="h-6 w-auto">
           <img src="@/assets/15.png" class="block h-full w-auto dark:hidden" alt="Logo" />
          <img src="@/assets/2.png" class="hidden h-full w-auto dark:block" alt="Logo" />
        </div>
      </router-link>

      <div class="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
        <router-link
          to="/app/palettes"
          class="group relative px-4 py-2 text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
          active-class="!text-zinc-900 dark:!text-white"
        >
          <span>Palettes</span>
          <span class="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-zinc-900 opacity-0 transition-opacity group-hover:opacity-100 dark:bg-white" :class="{ 'opacity-100': route.path === '/app/palettes' }"></span>
        </router-link>
        <router-link
          to="/pricing"
          class="group relative px-4 py-2 text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
          active-class="!text-zinc-900 dark:!text-white"
        >
          <span>Pricing</span>
          <span class="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-zinc-900 opacity-0 transition-opacity group-hover:opacity-100 dark:bg-white" :class="{ 'opacity-100': route.path === '/pricing' }"></span>
        </router-link>
        <a
          href="https://x.com/nexgridco"
          target="_blank"
          rel="noopener noreferrer"
          class="group relative px-4 py-2 text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
        >
          <span>Follow on X</span>
          <span class="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-zinc-900 opacity-0 transition-opacity group-hover:opacity-100 dark:bg-white"></span>
        </a>
      </div>

      <div class="flex items-center gap-2">
        <button
          @click="toggleTheme"
          class="group flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-white transition-all"
        >
          <span class="material-symbols-rounded text-[20px] transition-transform duration-500 group-hover:rotate-180">
            {{ !isDark ? 'light_mode' : 'dark_mode' }}
          </span>
        </button>
        <div class="hidden h-4 w-px bg-zinc-300 dark:bg-zinc-700 md:block mx-1"></div>
        <div class="hidden md:flex items-center gap-2">
          <template v-if="isAuthed">
            <button
              @click="signOut"
              class="rounded-full px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-white transition-all"
            >
              Sign out
            </button>
            <router-link
              to="/dashboard"
              class="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-zinc-900/20 transition-transform hover:scale-105 active:scale-95 dark:bg-white dark:text-zinc-900 dark:shadow-white/10"
            >
              <span>Dashboard</span>
            </router-link>
          </template>
          <template v-else>
            <button
              @click="openAuth"
              class="rounded-full px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-white transition-all"
            >
              Log in
            </button>
            <button
              @click="openAuth"
              class="inline-flex items-center gap-2 rounded-full bg-sky-500 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-sky-500/30 transition-transform hover:scale-105 hover:bg-sky-400 active:scale-95"
            >
              <span>Get Started</span>
            </button>
          </template>
        </div>
        <button
          @click="open = !open"
          class="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-zinc-900 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-white dark:hover:bg-zinc-700 md:hidden"
        >
          <span class="material-symbols-rounded text-[24px]">{{ open ? 'close' : 'menu' }}</span>
        </button>
      </div>
    </nav>
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform -translate-y-4 opacity-0 scale-95"
      enter-to-class="transform translate-y-0 opacity-100 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform translate-y-0 opacity-100 scale-100"
      leave-to-class="transform -translate-y-4 opacity-0 scale-95"
    >
      <div
        v-if="open"
        class="absolute top-[4.5rem] w-[calc(100%-2rem)] max-w-sm overflow-hidden rounded-3xl border border-white/20 bg-white/80 p-2 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-zinc-900/90 md:hidden"
      >
        <ul class="grid gap-1">
          <li>
            <router-link
              @click="closeMenu"
              to="/app/palettes"
              class="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-zinc-700 hover:bg-white/50 hover:shadow-sm dark:text-zinc-200 dark:hover:bg-white/10"
            >
              <span class="material-symbols-rounded text-[20px]">palette</span>
              Palettes
            </router-link>
          </li>
          <li>
            <router-link
              @click="closeMenu"
              to="/pricing"
              class="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-zinc-700 hover:bg-white/50 hover:shadow-sm dark:text-zinc-200 dark:hover:bg-white/10"
            >
              <span class="material-symbols-rounded text-[20px]">sell</span>
              Pricing
            </router-link>
          </li>
          <li>
            <a
              href="https://x.com/nexgridco"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-zinc-700 hover:bg-white/50 hover:shadow-sm dark:text-zinc-200 dark:hover:bg-white/10"
            >
              <svg class="h-5 w-5 fill-current" viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              Follow on X
            </a>
          </li>
        </ul>
        <div class="mt-2 border-t border-zinc-200/50 p-2 dark:border-white/10">
          <template v-if="isAuthed">
            <router-link
              @click="closeMenu"
              to="/dashboard"
              class="mb-2 flex w-full items-center justify-center rounded-2xl bg-zinc-900 py-3 text-sm font-semibold text-white dark:bg-white dark:text-zinc-900"
            >
              Open Dashboard
            </router-link>
            <button
              @click="signOutFromMenu"
              class="flex w-full items-center justify-center rounded-2xl border border-zinc-200 bg-white py-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            >
              Sign out
            </button>
          </template>
          <template v-else>
            <button
              @click="openAuthFromMenu"
              class="flex w-full items-center justify-center rounded-2xl bg-sky-500 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-500/25"
            >
              Sign in / Register
            </button>
          </template>
        </div>
      </div>
    </transition>
    <teleport to="body">
      <LoginPage
        v-if="['1','reset'].includes(String(route.query.auth || ''))"
        @close="closeAuth"
        @auth-success="onAuthSuccess"
      />
    </teleport>
  </div>
</template>
<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LoginPage from '@/views/LoginPage.vue'
const route = useRoute()
const router = useRouter()
const open = ref(false)
const isDark = ref(false)
const authVersion = ref(0)
const isAuthed = computed(() => {
  authVersion.value
  return !!(localStorage.getItem('token') || sessionStorage.getItem('token'))
})
function openAuth() {
  const q = { ...route.query, auth: '1' }
  router.replace({ path: route.path, query: q, hash: route.hash })
}
function openAuthFromMenu() {
  open.value = false
  openAuth()
}
function closeAuth() {
  const q = { ...route.query }
  delete q.auth
  delete q.token
  router.replace({ path: route.path, query: q, hash: route.hash })
}
function onAuthSuccess() {
  authVersion.value++
  closeAuth()
  router.push('/dashboard')
}
function signOut() {
  localStorage.clear()
  sessionStorage.clear()
  authVersion.value++
  router.push('/')
}
function signOutFromMenu() {
  open.value = false
  signOut()
}
function applyTheme() {
  document.documentElement.classList.toggle('dark', isDark.value)
}
function toggleTheme() {
  isDark.value = !isDark.value
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
  applyTheme()
}
function initTheme() {
  const saved = localStorage.getItem('theme')
  isDark.value = saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches
  applyTheme()
}
initTheme()
function closeMenu() {
  open.value = false
}
</script>
<style scoped>
.material-symbols-rounded {
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24;
}
.router-link-active {
  transition: all 0.3s ease;
}
</style>